/**
 * Eastern-time calendar helpers for attendance pages. Work dates and weekend
 * keys are Eastern YYYY-MM-DD strings on the backend, so the pages compute
 * them in that zone rather than the browser's.
 */
export const EASTERN_TIME_ZONE = 'America/New_York';

const ET_DATE_INPUT = new Intl.DateTimeFormat('en-CA', {
  timeZone: EASTERN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const ET_DATE_TIME = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TIME_ZONE,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

/** Today's Eastern calendar date as YYYY-MM-DD. */
export const getTodayEt = () => ET_DATE_INPUT.format(new Date());

export const addDaysToYmd = (ymd: string, days: number) => {
  const date = new Date(`${ymd}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/**
 * The Saturday that starts the weekend a date belongs to: Saturday is
 * itself, Sunday is the day before, and a weekday points to the coming
 * Saturday. Matches the backend's weekend keying.
 */
export const getWeekendStartForYmd = (ymd: string) => {
  const dayOfWeek = new Date(`${ymd}T12:00:00Z`).getUTCDay();
  if (dayOfWeek === 6) return ymd;
  if (dayOfWeek === 0) return addDaysToYmd(ymd, -1);
  return addDaysToYmd(ymd, 6 - dayOfWeek);
};

export const getUpcomingWeekendStartEt = () => getWeekendStartForYmd(getTodayEt());

/** "Oct 7, 2026, 8:47 AM ET", or an em dash when there is no value. */
export const formatEtDateTime = (value: string | null) => {
  if (!value) return '—';
  return `${ET_DATE_TIME.format(new Date(value))} ET`;
};

const SHORT_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** "Oct 7" for a YYYY-MM-DD calendar date (no timezone shift). */
export const formatYmdShort = (ymd: string) => SHORT_DATE.format(new Date(`${ymd}T12:00:00Z`));
